import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check, Package, Film, ExternalLink, Scale, Image, Palette, Music, Users, Headphones, Sliders, Ticket } from 'lucide-react';
import { cn } from '../lib/utils';

const DISTRIBUTION_PACKAGES = [
  {
    id: 'dist_none',
    name: 'Sin distribución adicional',
    price: 0,
    description: 'Solo distribución incluida en el paquete base',
    link: null
  },
  {
    id: 'dist_essential',
    name: 'Distribución Essential',
    price: 697,
    description: 'Distribuir únicamente en viubux.com',
    link: null
  },
  {
    id: 'dist_platino',
    name: 'Distribución Platino',
    price: 1697,
    description: 'Distribución multiplataforma en tiendas minor.',
    link: 'https://www.canva.com/design/DAGdjrfHNfo/Yi_mHDnQhdh4uU7YpGWoww/view?utm_content=DAGdjrfHNfo&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h0220496a74#5'
  },
  {
    id: 'dist_premium',
    name: 'Distribución Premium',
    price: 2997,
    description: 'Tiendas Majors = Audible, Bookmate, Kobo, Google Play, Apple Books, etc.',
    link: 'https://www.canva.com/design/DAGdjrfHNfo/Yi_mHDnQhdh4uU7YpGWoww/view?utm_content=DAGdjrfHNfo&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h0220496a74#6'
  }
];

const BOOKTRAILER_PACKAGES = [
  {
    id: 'bt_none',
    name: 'Sin Booktrailer',
    price: 0,
    description: 'No incluir booktrailer',
    link: null
  },
  {
    id: 'bt_essential',
    name: 'Booktrailer Essential',
    price: 697,
    description: 'Portada con AudioSample',
    link: null
  },
  {
    id: 'bt_platino',
    name: 'Booktrailer Platino',
    price: 4997,
    description: 'Motion Graphics animado',
    link: null
  },
  {
    id: 'bt_premium',
    name: 'Booktrailer Premium',
    price: 8997,
    description: 'Full Production con efectos cinematográficos',
    link: null
  }
];

const LEGAL_KIT_OPTIONS = [
  {
    id: 'legal_none',
    name: 'Sin Kit Legal',
    price: 0,
    description: 'No incluir servicios legales'
  },
  {
    id: 'legal_registro',
    name: 'Registro de Obra',
    price: 2500,
    description: 'Registro oficial de tu obra ante las autoridades'
  },
  {
    id: 'legal_isbn',
    name: 'Solicitud de ISBN',
    price: 2000,
    description: 'Gestión completa del ISBN para tu publicación'
  },
  {
    id: 'legal_combo',
    name: 'Paquete Combo (Registro + ISBN)',
    price: 4000,
    description: '¡Ahorra $500! Registro de Obra + Solicitud de ISBN'
  }
];

const COVER_DESIGN_OPTIONS = [
  {
    id: 'cover_none',
    name: 'Sin Diseño de Portada',
    price: 0,
    description: 'No incluir diseño de portada'
  },
  {
    id: 'cover_design',
    name: 'Diseño de Portada para Audiolibro',
    price: 1250,
    description: 'Diseño profesional optimizado para plataformas digitales'
  }
];

const BRANDING_PACK_OPTIONS = [
  {
    id: 'branding_none',
    name: 'Sin Pack Branding',
    price: 0,
    description: 'No incluir pack branding'
  },
  {
    id: 'branding_essential',
    name: 'Pack Branding Essential',
    price: 2500,
    description: 'Portada + 3 miniaturas para redes sociales'
  },
  {
    id: 'branding_pro',
    name: 'Pack Branding Pro',
    price: 4500,
    description: 'Portada + Banner promocional + 6 miniaturas + Kit para redes'
  },
  {
    id: 'branding_premium',
    name: 'Pack Branding Premium',
    price: 7500,
    description: 'Todo lo del Pro + Diseño de marca personal + 12 miniaturas + Material impreso'
  }
];

const MUSIC_STOCK_OPTIONS = [
  {
    id: 'music_none',
    name: 'Sin Música de Stock',
    price: 0,
    description: 'No incluir música de stock'
  },
  {
    id: 'music_stock',
    name: 'Música de Stock',
    price: 797,
    description: 'Licencia de música de stock por pieza',
    hasQuantity: true
  }
];

const MUSIC_UNIT_PRICE = 797; // Precio por pieza

const CONSULTORIA_OPTIONS = [
  {
    id: 'consultoria_none',
    name: 'Sin Consultoría',
    price: 0,
    description: 'No incluir consultoría personalizada'
  },
  {
    id: 'consultoria',
    name: 'Consultoría Personalizada',
    price: 850,
    description: 'Sesión de consultoría personalizada',
    hasQuantity: true
  }
];

const CONSULTORIA_UNIT_PRICE = 850; // Precio por sesión

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-[#080c2d]/50 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

function AddonOption({ option, isSelected, onSelect, formatPrice, currency }) {
  const handleLinkClick = (e) => {
    e.stopPropagation();
  };

  return (
    <button
      onClick={() => onSelect(option.id)}
      className={cn(
        "w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left",
        isSelected
          ? "border-[#aa2aff] bg-[#aa2aff]/10"
          : "border-white/5 hover:border-white/20 bg-white/5"
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
          isSelected ? "border-[#aa2aff] bg-[#aa2aff]" : "border-slate-500"
        )}>
          {isSelected && <Check className="w-3 h-3 text-white" />}
        </div>
        <div>
          <p className={cn(
            "font-medium text-sm",
            isSelected ? "text-[#aa2aff]" : "text-white"
          )}>
            {option.name}
          </p>
          <p className="text-xs text-slate-400">
            {option.description}
            {option.link && (
              <a 
                href={option.link} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                className="inline-flex items-center gap-1 ml-1 text-[#aa2aff] hover:underline"
              >
                Ver más <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </p>
        </div>
      </div>
      <span className={cn(
        "font-bold text-sm flex-shrink-0",
        option.price === 0 ? "text-slate-500" : isSelected ? "text-[#aa2aff]" : "text-white"
      )}>
        {option.price === 0 ? 'Incluido' : `${formatPrice(option.price)} ${currency}`}
      </span>
    </button>
  );
}

export function AddonsSelector({ 
  selectedDistribution, 
  selectedBooktrailer,
  selectedLegalKit,
  selectedCoverDesign,
  selectedBrandingPack,
  selectedMusicStock,
  musicStockQuantity = 1,
  selectedConsultoria,
  consultoriaQuantity = 1,
  // New services
  selectedQrDinamico = 'qr_none',
  selectedEnlaceGo = 'enlace_none',
  selectedNfcVcard = 'nfc_none',
  selectedSeparadores = 'sep_none',
  selectedPreventa = 'preventa_none',
  selectedBannerPromotion = 'banner_none',
  bannerPromotionMonths = 1,
  selectedDisenoWebBanner = 'web_banner_none',
  selectedPhotoshoot = 'photoshoot_none',
  selectedPaginaAutor = 'pagina_autor_none',
  selectedPautajeDigital = 'pautaje_none',
  selectedCommissionFree = 'commission_free_none',
  // Impresión services
  selectedTarjetas = 'tarjetas_none',
  selectedSeparadoresImpresos = 'sep_impresos_none',
  selectedEnvio = 'envio_none',
  // Callbacks
  onDistributionChange, 
  onBooktrailerChange,
  onLegalKitChange,
  onCoverDesignChange,
  onBrandingPackChange,
  onMusicStockChange,
  onMusicStockQuantityChange,
  onConsultoriaChange,
  onConsultoriaQuantityChange,
  onQrDinamicoChange,
  onEnlaceGoChange,
  onNfcVcardChange,
  onSeparadoresChange,
  onPreventaChange,
  onBannerPromotionChange,
  onBannerPromotionMonthsChange,
  onDisenoWebBannerChange,
  onPhotoshootChange,
  onPaginaAutorChange,
  onPautajeDigitalChange,
  onCommissionFreeChange,
  onTarjetasChange,
  onSeparadoresImpresosChange,
  onEnvioChange,
  // Revision & Masterizacion
  revisionAudio = 'rev_none',
  onRevisionAudioChange,
  masterizacion = 'master_none',
  onMasterizacionChange,
  formatPrice,
  currency,
  servicePrices = {}
}) {
  const [distOpen, setDistOpen] = useState(false);
  const [btOpen, setBtOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const [consultoriaOpen, setConsultoriaOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [enlaceOpen, setEnlaceOpen] = useState(false);
  const [nfcOpen, setNfcOpen] = useState(false);
  const [separadoresOpen, setSeparadoresOpen] = useState(false);
  const [preventaOpen, setPreventaOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [webBannerOpen, setWebBannerOpen] = useState(false);
  const [photoshootOpen, setPhotoshootOpen] = useState(false);
  const [paginaAutorOpen, setPaginaAutorOpen] = useState(false);
  const [pautajeDigitalOpen, setPautajeDigitalOpen] = useState(false);
  const [commissionFreeOpen, setCommissionFreeOpen] = useState(false);
  const [tarjetasOpen, setTarjetasOpen] = useState(false);
  const [separadoresImpresosOpen, setSeparadoresImpresosOpen] = useState(false);
  const [envioOpen, setEnvioOpen] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [masterizacionOpen, setMasterizacionOpen] = useState(false);

  // Build dynamic packages with prices from settings
  const distributionPackages = [
    { id: 'dist_none', name: 'Sin distribución adicional', price: 0, description: 'Solo distribución incluida en el paquete base' },
    { id: 'dist_essential', name: 'Distribución Essential', price: servicePrices.price_dist_essential || 697, description: 'Distribuir únicamente en viubux.com' },
    { id: 'dist_platino', name: 'Distribución Platino', price: servicePrices.price_dist_platino || 1697, description: 'Distribución multiplataforma en tiendas minor.', link: 'https://www.canva.com/design/DAGdjrfHNfo/Yi_mHDnQhdh4uU7YpGWoww/view?utm_content=DAGdjrfHNfo&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h0220496a74#5' },
    { id: 'dist_premium', name: 'Distribución Premium', price: servicePrices.price_dist_premium || 2997, description: 'Tiendas Majors = Audible, Bookmate, Kobo, Google Play, Apple Books, etc.', link: 'https://www.canva.com/design/DAGdjrfHNfo/Yi_mHDnQhdh4uU7YpGWoww/view?utm_content=DAGdjrfHNfo&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h0220496a74#6' }
  ];

  const booktrailerPackages = [
    { id: 'bt_none', name: 'Sin Booktrailer', price: 0, description: 'No incluir booktrailer' },
    { id: 'bt_essential', name: 'Booktrailer Essential', price: servicePrices.price_bt_essential || 697, description: 'Portada con AudioSample' },
    { id: 'bt_platino', name: 'Booktrailer Platino', price: servicePrices.price_bt_platino || 4997, description: 'Motion Graphics animado' },
    { id: 'bt_premium', name: 'Booktrailer Premium', price: servicePrices.price_bt_premium || 8997, description: 'Full Production con efectos cinematográficos' }
  ];

  const legalKitOptions = [
    { id: 'legal_none', name: 'Sin Kit Legal', price: 0, description: 'No incluir servicios legales' },
    { id: 'legal_registro', name: 'Registro de Obra', price: servicePrices.price_legal_registro || 2500, description: 'Registro oficial de tu obra ante las autoridades' },
    { id: 'legal_isbn', name: 'Solicitud de ISBN', price: servicePrices.price_legal_isbn || 2000, description: 'Gestión completa del ISBN para tu publicación' },
    { id: 'legal_combo', name: 'Paquete Combo (Registro + ISBN)', price: servicePrices.price_legal_combo || 4000, description: '¡Ahorra $500! Registro de Obra + Solicitud de ISBN' }
  ];

  const coverDesignOptions = [
    { id: 'cover_none', name: 'Sin Diseño de Portada', price: 0, description: 'No incluir diseño de portada' },
    { id: 'cover_design', name: 'Diseño de Portada para Audiolibro', price: servicePrices.price_cover_design || 1250, description: 'Diseño profesional optimizado para plataformas digitales' }
  ];

  const brandingPackOptions = [
    { id: 'branding_none', name: 'Sin Pack Branding', price: 0, description: 'No incluir pack branding' },
    { id: 'branding_essential', name: 'Pack Branding Essential', price: servicePrices.price_branding_essential || 2500, description: 'Portada + 3 miniaturas para redes sociales' },
    { id: 'branding_pro', name: 'Pack Branding Pro', price: servicePrices.price_branding_pro || 4500, description: 'Portada + Banner promocional + 6 miniaturas + Kit para redes' },
    { id: 'branding_premium', name: 'Pack Branding Premium', price: servicePrices.price_branding_premium || 7500, description: 'Todo lo del Pro + Diseño de marca personal + 12 miniaturas + Material impreso' }
  ];

  const musicUnitPrice = servicePrices.price_music_stock || 797;
  const musicStockOptions = [
    { id: 'music_none', name: 'Sin Música de Stock', price: 0, description: 'No incluir música de stock' },
    { id: 'music_stock', name: 'Música de Stock', price: musicUnitPrice, description: 'Licencia de música de stock por pieza', hasQuantity: true }
  ];

  const consultoriaUnitPrice = servicePrices.price_consultoria || 850;
  const consultoriaOptions = [
    { id: 'consultoria_none', name: 'Sin Consultoría', price: 0, description: 'No incluir consultoría personalizada' },
    { id: 'consultoria', name: 'Consultoría Personalizada', price: consultoriaUnitPrice, description: 'Sesión de consultoría personalizada', hasQuantity: true }
  ];

  const selectedDistOption = distributionPackages.find(p => p.id === selectedDistribution);
  const selectedBtOption = booktrailerPackages.find(p => p.id === selectedBooktrailer);
  const selectedLegalOption = legalKitOptions.find(p => p.id === selectedLegalKit);
  const selectedCoverOption = coverDesignOptions.find(p => p.id === selectedCoverDesign);
  const selectedBrandingOption = brandingPackOptions.find(p => p.id === selectedBrandingPack);
  const selectedMusicOption = musicStockOptions.find(p => p.id === selectedMusicStock);
  const selectedConsultoriaOption = consultoriaOptions.find(p => p.id === selectedConsultoria);

  // New services with dynamic prices
  const qrDinamicoOptions = [
    { id: 'qr_none', name: 'Sin QR Dinámico', price: 0, description: 'No incluir QR dinámico' },
    { id: 'qr_dinamico', name: 'QR Dinámico', price: servicePrices.price_qr_dinamico || 397, description: 'Código QR dinámico personalizado' }
  ];
  const enlaceGoOptions = [
    { id: 'enlace_none', name: 'Sin Enlace Único', price: 0, description: 'No incluir enlace único' },
    { id: 'enlace_go', name: 'Enlace Único Go.Viubux.com', price: servicePrices.price_enlace_go || 197, description: 'Tu enlace personalizado Go.Viubux.com' }
  ];
  const nfcVcardOptions = [
    { id: 'nfc_none', name: 'Sin NFC VCard', price: 0, description: 'No incluir NFC VCard' },
    { id: 'nfc_vcard', name: 'Digital NFC VCard / LinkTree', price: servicePrices.price_nfc_vcard || 1997, description: 'Setup + Renovación anual $500 MXN' }
  ];
  const separadoresOptions = [
    { id: 'sep_none', name: 'Sin Separadores', price: 0, description: 'No incluir separadores' },
    { id: 'separadores', name: 'Separadores de Libro para Imprimir', price: servicePrices.price_separadores || 397, description: 'Diseño de separadores para imprimir' }
  ];
  const preventaOptions = [
    { id: 'preventa_none', name: 'Sin Preventa', price: 0, description: 'No activar preventa' },
    { id: 'preventa', name: 'Activar Preventa', price: servicePrices.price_preventa || 897, description: 'Activa la preventa de tu obra' }
  ];
  const bannerPromotionMonthPrice = servicePrices.price_banner_promotion || 197;
  const bannerPromotionOptions = [
    { id: 'banner_none', name: 'Sin Banner Promotion', price: 0, description: 'No incluir banner promocional' },
    { id: 'banner_promotion', name: 'Banner Promotion', price: bannerPromotionMonthPrice, description: `$${bannerPromotionMonthPrice}/mes (hasta 6 meses)`, hasQuantity: true }
  ];
  const disenoWebBannerOptions = [
    { id: 'web_banner_none', name: 'Sin Diseño WEB Banner', price: 0, description: 'No incluir diseño de web banner' },
    { id: 'diseno_web_banner', name: 'Diseño de WEB Banner', price: servicePrices.price_diseno_web_banner || 997, description: 'Diseño profesional de banner para web' }
  ];
  const photoshootOptions = [
    { id: 'photoshoot_none', name: 'Sin Photoshoot', price: 0, description: 'No incluir photoshoot' },
    { id: 'photoshoot', name: 'Photoshoot 1hr', price: servicePrices.price_photoshoot || 2500, description: 'Sesión fotográfica profesional de 1 hora' }
  ];
  const paginaAutorOptions = [
    { id: 'pagina_autor_none', name: 'Sin Página de Autor', price: 0, description: 'No incluir página de autor' },
    { id: 'pagina_autor', name: 'Página de Autor', price: servicePrices.price_pagina_autor || 3500, description: 'Página web personalizada de autor' }
  ];
  const pautajeDigitalOptions = [
    { id: 'pautaje_none', name: 'Sin Pautaje Digital', price: 0, description: 'No incluir pautaje digital' },
    { id: 'pautaje_digital', name: 'Pautaje Digital', price: servicePrices.price_pautaje_digital || 2000, description: '2 Meses en Meta y Google Ads. Debes tener listos promocionales y/o tu BookTrailer. Incluye 20% por gestión ($1,600 se destinan a pauta).' }
  ];
  const commissionFreeOptions = [
    { id: 'commission_free_none', name: 'Sin Commission-Free Pass', price: 0, description: 'No incluir' },
    { id: 'commission_free', name: 'Commission-Free Monthly Pass', price: servicePrices.price_commission_free || 1500, description: 'Con este paquete, durante un mes podrás gozar de un cashback del 33% sobre todas las ventas que recibas en app.Viubux.com y play.viubux.com durante el mes de tu contratación (no aplica en modelos de suscripción, sólo en venta directa). Un ahorro del 33%, totalmente reembolsable, sumado a tu Reporte de regalías. Diseñado para que tomes acción masiva y aproveches el impulso que Viubux te da para invertir en campañas de marketing. En Viubux queremos apoyarte.' }
  ];

  return (
    <div className="space-y-3" data-testid="addons-selector">
      {/* Distribution Packages */}
      <CollapsibleSection
        title={`Distribución ${selectedDistOption?.price > 0 ? `(+${formatPrice(selectedDistOption.price)})` : ''}`}
        icon={Package}
        isOpen={distOpen}
        onToggle={() => setDistOpen(!distOpen)}
      >
        {distributionPackages.map(option => (
          <AddonOption
            key={option.id}
            option={option}
            isSelected={selectedDistribution === option.id}
            onSelect={onDistributionChange}
            formatPrice={formatPrice}
            currency={currency}
          />
        ))}
      </CollapsibleSection>

      {/* Revisión de Archivos de Audio para Distribución */}
      <CollapsibleSection
        title={`Revisión de Audio ${revisionAudio !== 'rev_none' ? `(+${formatPrice(
          revisionAudio === 'rev_7' ? 697 : revisionAudio === 'rev_10' ? 997 : revisionAudio === 'rev_20' ? 1497 : 0
        )})` : ''}`}
        icon={Headphones}
        isOpen={revisionOpen}
        onToggle={() => setRevisionOpen(!revisionOpen)}
      >
        {[
          { id: 'rev_none', name: 'Sin Revisión', price: 0, description: 'No incluir revisión de archivos' },
          { id: 'rev_7', name: '0 - 7 horas', price: 697, description: 'Revisión para audiolibros de hasta 7 horas' },
          { id: 'rev_10', name: '7.1 - 10 horas', price: 997, description: 'Revisión para audiolibros de 7.1 a 10 horas' },
          { id: 'rev_20', name: '10.1 - 20 horas', price: 1497, description: 'Revisión para audiolibros de 10.1 a 20 horas' }
        ].map(option => (
          <AddonOption key={option.id} option={option} isSelected={revisionAudio === option.id} onSelect={onRevisionAudioChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Masterización de Audiolibro */}
      <CollapsibleSection
        title={`Masterización ${masterizacion !== 'master_none' ? `(+${formatPrice(
          masterizacion === 'master_3' ? 1000 : masterizacion === 'master_7' ? 1500 : masterizacion === 'master_10' ? 1850 : masterizacion === 'master_20' ? 2500 : 0
        )})` : ''}`}
        icon={Sliders}
        isOpen={masterizacionOpen}
        onToggle={() => setMasterizacionOpen(!masterizacionOpen)}
      >
        {[
          { id: 'master_none', name: 'Sin Masterización', price: 0, description: 'No incluir masterización' },
          { id: 'master_3', name: '0 - 3.5 horas', price: 1000, description: 'Masterización hasta 3.5 horas' },
          { id: 'master_7', name: '3.6 - 7 horas', price: 1500, description: 'Masterización de 3.6 a 7 horas' },
          { id: 'master_10', name: '7.1 - 10 horas', price: 1850, description: 'Masterización de 7.1 a 10 horas' },
          { id: 'master_20', name: '10.1 - 20 horas', price: 2500, description: 'Masterización de 10.1 a 20 horas' }
        ].map(option => (
          <AddonOption key={option.id} option={option} isSelected={masterizacion === option.id} onSelect={onMasterizacionChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Booktrailer Packages */}
      <CollapsibleSection
        title={`Booktrailer ${selectedBtOption?.price > 0 ? `(+${formatPrice(selectedBtOption.price)})` : ''}`}
        icon={Film}
        isOpen={btOpen}
        onToggle={() => setBtOpen(!btOpen)}
      >
        {booktrailerPackages.map(option => (
          <AddonOption
            key={option.id}
            option={option}
            isSelected={selectedBooktrailer === option.id}
            onSelect={onBooktrailerChange}
            formatPrice={formatPrice}
            currency={currency}
          />
        ))}
      </CollapsibleSection>

      {/* Legal Kit */}
      <CollapsibleSection
        title={`Kit Legal ${selectedLegalOption?.price > 0 ? `(+${formatPrice(selectedLegalOption.price)})` : ''}`}
        icon={Scale}
        isOpen={legalOpen}
        onToggle={() => setLegalOpen(!legalOpen)}
      >
        {legalKitOptions.map(option => (
          <AddonOption
            key={option.id}
            option={option}
            isSelected={selectedLegalKit === option.id}
            onSelect={onLegalKitChange}
            formatPrice={formatPrice}
            currency={currency}
          />
        ))}
      </CollapsibleSection>

      {/* Cover Design */}
      <CollapsibleSection
        title={`Diseño de Portada ${selectedCoverOption?.price > 0 ? `(+${formatPrice(selectedCoverOption.price)})` : ''}`}
        icon={Image}
        isOpen={coverOpen}
        onToggle={() => setCoverOpen(!coverOpen)}
      >
        {coverDesignOptions.map(option => (
          <AddonOption
            key={option.id}
            option={option}
            isSelected={selectedCoverDesign === option.id}
            onSelect={onCoverDesignChange}
            formatPrice={formatPrice}
            currency={currency}
          />
        ))}
      </CollapsibleSection>

      {/* Branding Pack */}
      <CollapsibleSection
        title={`Pack Branding ${selectedBrandingOption?.price > 0 ? `(+${formatPrice(selectedBrandingOption.price)})` : ''}`}
        icon={Palette}
        isOpen={brandingOpen}
        onToggle={() => setBrandingOpen(!brandingOpen)}
      >
        {brandingPackOptions.map(option => (
          <AddonOption
            key={option.id}
            option={option}
            isSelected={selectedBrandingPack === option.id}
            onSelect={onBrandingPackChange}
            formatPrice={formatPrice}
            currency={currency}
          />
        ))}
      </CollapsibleSection>

      {/* Music Stock */}
      <CollapsibleSection
        title={`Música de Stock ${selectedMusicStock === 'music_stock' ? `(+${formatPrice(musicUnitPrice * musicStockQuantity)})` : ''}`}
        icon={Music}
        isOpen={musicOpen}
        onToggle={() => setMusicOpen(!musicOpen)}
      >
        {musicStockOptions.map(option => (
          <AddonOption
            key={option.id}
            option={option}
            isSelected={selectedMusicStock === option.id}
            onSelect={onMusicStockChange}
            formatPrice={formatPrice}
            currency={currency}
          />
        ))}
        
        {/* Quantity selector for music stock */}
        {selectedMusicStock === 'music_stock' && (
          <div className="mt-3 p-3 bg-[#aa2aff]/10 rounded-lg border border-[#aa2aff]/30">
            <div className="flex items-center justify-between">
              <label className="text-sm text-white font-medium">Cantidad de piezas:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onMusicStockQuantityChange(Math.max(1, musicStockQuantity - 1))}
                  className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 text-white font-bold transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={musicStockQuantity}
                  onChange={(e) => onMusicStockQuantityChange(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="w-16 text-center bg-[#080c2d] border border-white/20 rounded text-white py-1"
                  data-testid="input-music-quantity"
                />
                <button
                  onClick={() => onMusicStockQuantityChange(Math.min(50, musicStockQuantity + 1))}
                  className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 text-white font-bold transition-colors"
                >
                  +
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Total: {formatPrice(musicUnitPrice * musicStockQuantity)} {currency} ({musicStockQuantity} pieza{musicStockQuantity > 1 ? 's' : ''} × {formatPrice(musicUnitPrice)})
            </p>
          </div>
        )}
      </CollapsibleSection>

      {/* Consultoría Personalizada */}
      <CollapsibleSection
        title={`Consultoría ${selectedConsultoria === 'consultoria' ? `(+${formatPrice(consultoriaUnitPrice * consultoriaQuantity)})` : ''}`}
        icon={Users}
        isOpen={consultoriaOpen}
        onToggle={() => setConsultoriaOpen(!consultoriaOpen)}
      >
        {consultoriaOptions.map(option => (
          <AddonOption
            key={option.id}
            option={option}
            isSelected={selectedConsultoria === option.id}
            onSelect={onConsultoriaChange}
            formatPrice={formatPrice}
            currency={currency}
          />
        ))}
        
        {/* Quantity selector for consultoria */}
        {selectedConsultoria === 'consultoria' && (
          <div className="mt-3 p-3 bg-[#aa2aff]/10 rounded-lg border border-[#aa2aff]/30">
            <div className="flex items-center justify-between">
              <label className="text-sm text-white font-medium">Cantidad de sesiones:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onConsultoriaQuantityChange(Math.max(1, consultoriaQuantity - 1))}
                  className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 text-white font-bold transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={consultoriaQuantity}
                  onChange={(e) => onConsultoriaQuantityChange(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="w-16 text-center bg-[#080c2d] border border-white/20 rounded text-white py-1"
                  data-testid="input-consultoria-quantity"
                />
                <button
                  onClick={() => onConsultoriaQuantityChange(Math.min(50, consultoriaQuantity + 1))}
                  className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 text-white font-bold transition-colors"
                >
                  +
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Total: {formatPrice(consultoriaUnitPrice * consultoriaQuantity)} {currency} ({consultoriaQuantity} sesión{consultoriaQuantity > 1 ? 'es' : ''} × {formatPrice(consultoriaUnitPrice)})
            </p>
          </div>
        )}
      </CollapsibleSection>

      {/* === NEW SERVICES === */}

      {/* QR Dinámico */}
      <CollapsibleSection
        title={`QR Dinámico ${selectedQrDinamico === 'qr_dinamico' ? `(+${formatPrice(qrDinamicoOptions[1].price)})` : ''}`}
        icon={Package}
        isOpen={qrOpen}
        onToggle={() => setQrOpen(!qrOpen)}
      >
        {qrDinamicoOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedQrDinamico === option.id} onSelect={onQrDinamicoChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Enlace Único Go.Viubux */}
      <CollapsibleSection
        title={`Enlace Go.Viubux ${selectedEnlaceGo === 'enlace_go' ? `(+${formatPrice(enlaceGoOptions[1].price)})` : ''}`}
        icon={ExternalLink}
        isOpen={enlaceOpen}
        onToggle={() => setEnlaceOpen(!enlaceOpen)}
      >
        {enlaceGoOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedEnlaceGo === option.id} onSelect={onEnlaceGoChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* NFC VCard */}
      <CollapsibleSection
        title={`NFC VCard ${selectedNfcVcard === 'nfc_vcard' ? `(+${formatPrice(nfcVcardOptions[1].price)})` : ''}`}
        icon={Package}
        isOpen={nfcOpen}
        onToggle={() => setNfcOpen(!nfcOpen)}
      >
        {nfcVcardOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedNfcVcard === option.id} onSelect={onNfcVcardChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Separadores de libro */}
      <CollapsibleSection
        title={`Separadores ${selectedSeparadores === 'separadores' ? `(+${formatPrice(separadoresOptions[1].price)})` : ''}`}
        icon={Package}
        isOpen={separadoresOpen}
        onToggle={() => setSeparadoresOpen(!separadoresOpen)}
      >
        {separadoresOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedSeparadores === option.id} onSelect={onSeparadoresChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Activar Preventa */}
      <CollapsibleSection
        title={`Preventa ${selectedPreventa === 'preventa' ? `(+${formatPrice(preventaOptions[1].price)})` : ''}`}
        icon={Package}
        isOpen={preventaOpen}
        onToggle={() => setPreventaOpen(!preventaOpen)}
      >
        {preventaOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedPreventa === option.id} onSelect={onPreventaChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Banner Promotion */}
      <CollapsibleSection
        title={`Banner Promotion ${selectedBannerPromotion === 'banner_promotion' ? `(+${formatPrice(bannerPromotionMonthPrice * bannerPromotionMonths)})` : ''}`}
        icon={Image}
        isOpen={bannerOpen}
        onToggle={() => setBannerOpen(!bannerOpen)}
      >
        {bannerPromotionOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedBannerPromotion === option.id} onSelect={onBannerPromotionChange} formatPrice={formatPrice} currency={currency} />
        ))}
        {selectedBannerPromotion === 'banner_promotion' && (
          <div className="mt-3 p-3 bg-[#aa2aff]/10 rounded-lg border border-[#aa2aff]/30">
            <div className="flex items-center justify-between">
              <label className="text-sm text-white font-medium">Meses:</label>
              <div className="flex items-center gap-2">
                <button onClick={() => onBannerPromotionMonthsChange(Math.max(1, bannerPromotionMonths - 1))} className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 text-white font-bold transition-colors">-</button>
                <input type="number" min="1" max="6" value={bannerPromotionMonths} onChange={(e) => onBannerPromotionMonthsChange(Math.max(1, Math.min(6, parseInt(e.target.value) || 1)))} className="w-16 text-center bg-[#080c2d] border border-white/20 rounded text-white py-1" data-testid="input-banner-months" />
                <button onClick={() => onBannerPromotionMonthsChange(Math.min(6, bannerPromotionMonths + 1))} className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 text-white font-bold transition-colors">+</button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Total: {formatPrice(bannerPromotionMonthPrice * bannerPromotionMonths)} {currency} ({bannerPromotionMonths} mes{bannerPromotionMonths > 1 ? 'es' : ''} × {formatPrice(bannerPromotionMonthPrice)})
            </p>
          </div>
        )}
      </CollapsibleSection>

      {/* Diseño WEB Banner */}
      <CollapsibleSection
        title={`Diseño WEB Banner ${selectedDisenoWebBanner === 'diseno_web_banner' ? `(+${formatPrice(disenoWebBannerOptions[1].price)})` : ''}`}
        icon={Image}
        isOpen={webBannerOpen}
        onToggle={() => setWebBannerOpen(!webBannerOpen)}
      >
        {disenoWebBannerOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedDisenoWebBanner === option.id} onSelect={onDisenoWebBannerChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Photoshoot */}
      <CollapsibleSection
        title={`Photoshoot ${selectedPhotoshoot === 'photoshoot' ? `(+${formatPrice(photoshootOptions[1].price)})` : ''}`}
        icon={Image}
        isOpen={photoshootOpen}
        onToggle={() => setPhotoshootOpen(!photoshootOpen)}
      >
        {photoshootOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedPhotoshoot === option.id} onSelect={onPhotoshootChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Página de Autor */}
      <CollapsibleSection
        title={`Página de Autor ${selectedPaginaAutor === 'pagina_autor' ? `(+${formatPrice(paginaAutorOptions[1].price)})` : ''}`}
        icon={ExternalLink}
        isOpen={paginaAutorOpen}
        onToggle={() => setPaginaAutorOpen(!paginaAutorOpen)}
      >
        {paginaAutorOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedPaginaAutor === option.id} onSelect={onPaginaAutorChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Pautaje Digital */}
      <CollapsibleSection
        title={`Pautaje Digital ${selectedPautajeDigital === 'pautaje_digital' ? `(+${formatPrice(pautajeDigitalOptions[1].price)})` : ''}`}
        icon={ExternalLink}
        isOpen={pautajeDigitalOpen}
        onToggle={() => setPautajeDigitalOpen(!pautajeDigitalOpen)}
      >
        {pautajeDigitalOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedPautajeDigital === option.id} onSelect={onPautajeDigitalChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Commission-Free Monthly Pass */}
      <CollapsibleSection
        title={`Commission-Free Monthly Pass ${selectedCommissionFree === 'commission_free' ? `(+${formatPrice(commissionFreeOptions[1].price)})` : ''}`}
        icon={Ticket}
        isOpen={commissionFreeOpen}
        onToggle={() => setCommissionFreeOpen(!commissionFreeOpen)}
      >
        {commissionFreeOptions.map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedCommissionFree === option.id} onSelect={onCommissionFreeChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Tarjetas de Presentación */}
      <CollapsibleSection
        title={`Tarjetas de Presentación ${selectedTarjetas === 'tarjetas' ? `(+${formatPrice(servicePrices.price_tarjetas || 697)})` : ''}`}
        icon={Package}
        isOpen={tarjetasOpen}
        onToggle={() => setTarjetasOpen(!tarjetasOpen)}
      >
        {[
          { id: 'tarjetas_none', name: 'Sin Tarjetas', price: 0, description: 'No incluir tarjetas' },
          { id: 'tarjetas', name: '1000 Tarjetas de Presentación', price: servicePrices.price_tarjetas || 697, description: 'Color 4x4, 1000 piezas' }
        ].map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedTarjetas === option.id} onSelect={onTarjetasChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Separadores Impresos */}
      <CollapsibleSection
        title={`Separadores Impresos ${selectedSeparadoresImpresos === 'sep_impresos' ? `(+${formatPrice(servicePrices.price_separadores_impresos || 797)})` : ''}`}
        icon={Package}
        isOpen={separadoresImpresosOpen}
        onToggle={() => setSeparadoresImpresosOpen(!separadoresImpresosOpen)}
      >
        {[
          { id: 'sep_impresos_none', name: 'Sin Separadores Impresos', price: 0, description: 'No incluir separadores impresos' },
          { id: 'sep_impresos', name: 'Separadores de Libro Impresos', price: servicePrices.price_separadores_impresos || 797, description: 'Color 4x4, 500 piezas' }
        ].map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedSeparadoresImpresos === option.id} onSelect={onSeparadoresImpresosChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>

      {/* Envío a Domicilio */}
      <CollapsibleSection
        title={`Envío a Domicilio ${selectedEnvio === 'envio' ? `(+${formatPrice(servicePrices.price_envio || 150)})` : ''}`}
        icon={Package}
        isOpen={envioOpen}
        onToggle={() => setEnvioOpen(!envioOpen)}
      >
        {[
          { id: 'envio_none', name: 'Sin Envío', price: 0, description: 'Recolección en punto' },
          { id: 'envio', name: 'Envío a Domicilio', price: servicePrices.price_envio || 150, description: 'Envío nacional' }
        ].map(option => (
          <AddonOption key={option.id} option={option} isSelected={selectedEnvio === option.id} onSelect={onEnvioChange} formatPrice={formatPrice} currency={currency} />
        ))}
      </CollapsibleSection>
    </div>
  );
}

// Export constants for use in other components (legacy - now dynamic)
export { DISTRIBUTION_PACKAGES, BOOKTRAILER_PACKAGES, LEGAL_KIT_OPTIONS, COVER_DESIGN_OPTIONS, BRANDING_PACK_OPTIONS, MUSIC_STOCK_OPTIONS, MUSIC_UNIT_PRICE, CONSULTORIA_OPTIONS, CONSULTORIA_UNIT_PRICE };

export default AddonsSelector;
